import React, { useEffect, useMemo } from 'react';

import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useDebounce } from 'hooks/useDebaunce';
import useHashmapConverter from 'hooks/useHashmapConverter';

import { useQueryGetMilestoneFragnet, useQueryGetSingleMilestoneFragnet } from 'api/queries/getMilestoneFragnet.query';

import { milestoneFragnetMapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { MyCustomSelect } from 'components/common';

const mapper = (item) => ({
    label: `${item.name} (ID: ${
        item.activity_id.length > 20 ? item.activity_id.slice(0, 20) + '...' : item.activity_id
    }) (~${item.preceding_activities} related activities)`,
    value: item.task_version_hash_code,
});

export const TargetActivitySelect = ({ methods, name }) => {
    const hashcode = Object.values(useParams<{ id: string }>()).filter(Boolean).join(',');
    const { versionList: versionFromHashMap } = useHashmapConverter(hashcode);
    const latestVersionId = versionFromHashMap[0]?.versionDbId;
    const [filter, setFilter] = React.useState('');
    const debouncedValue = useDebounce<string>(filter, 1000);
    const { data: milestoneFragnet, isFetching } = useQueryGetMilestoneFragnet({
        latestVersion: latestVersionId,
        filter: debouncedValue,
    });

    const options = useMemo<ISelectOption<string>[]>(
        () => (milestoneFragnet ? milestoneFragnet.map(mapper) : []),
        [milestoneFragnet],
    );

    const { data: milestoneSingleFragnet } = useQueryGetSingleMilestoneFragnet({
        latestVersion: latestVersionId,
        taskVersionHashCode: methods?.getValues(name), // make sure BE can perform search by task_version_hash_code
    });

    const handleInputChangePrimary = (inputText, event) => {
        if (event.action !== 'input-blur' && event.action !== 'menu-close' && event.action !== 'set-value') {
            setFilter(inputText);
        }
        if (event.action === 'menu-close') {
            setFilter('');
        }
    };

    const handleChange = (value: ISelectOption<string> | null) => {
        methods?.setValue(name, value ? value.value : null, { shouldValidate: true, shouldDirty: true });
        methods?.setValue('taskVersionLabel', value ? value.label : null, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const handleValue = (value) => {
        if (!value) {
            return null;
        }
        if (
            milestoneSingleFragnet &&
            milestoneSingleFragnet.length > 0 &&
            milestoneFragnetMapper(milestoneSingleFragnet[0]).value === value
        ) {
            return milestoneFragnetMapper(milestoneSingleFragnet[0]);
        }
        return milestoneFragnet?.map(milestoneFragnetMapper).find((item) => item.value === value) || null;
    };

    useEffect(() => {
        if (milestoneFragnet && milestoneFragnet.length > 0 && !methods.getValues(name)) {
            methods?.setValue(name, milestoneFragnet[0].task_version_hash_code, {
                shouldValidate: true,
                shouldDirty: true,
            });
            methods?.setValue('taskVersionLabel', milestoneFragnet[0].name, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
    }, [milestoneFragnet]);

    return (
        <Controller
            render={({ field }) => (
                <MyCustomSelect<ISelectOption<string>>
                    {...field}
                    options={options}
                    id={'global-filter-component-fragnet-milestones'}
                    onInputChange={handleInputChangePrimary}
                    isClearable={true}
                    isLoading={isFetching}
                    onChange={handleChange}
                    value={handleValue(field.value)}
                    placeholder={'Select activity or start typing its name...'}
                />
            )}
            name={name}
            control={methods?.control}
        />
    );
};
